import { type HTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { getScoreColor } from '../theme/tokens';

export interface ScoreRingProps extends HTMLAttributes<HTMLDivElement> {
  /** Score value */
  score: number;
  /** Maximum score (default 10) */
  max?: number;
  /** Ring size */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show the score label */
  showLabel?: boolean;
  /** Custom color (overrides automatic) */
  color?: string;
  /** Label text (default is score/max) */
  label?: string;
}

const sizeConfig = {
  sm: { diameter: 40, strokeWidth: 4, fontSize: 'text-xs' },
  md: { diameter: 64, strokeWidth: 5, fontSize: 'text-base' },
  lg: { diameter: 96, strokeWidth: 6, fontSize: 'text-xl' },
};

/**
 * Circular score display with animated progress ring
 *
 * @example
 * ```tsx
 * <ScoreRing score={8} max={10} />
 * <ScoreRing score={75} max={100} size="lg" showLabel />
 * ```
 */
export function ScoreRing({
  score,
  max = 10,
  size = 'md',
  showLabel = true,
  color,
  label,
  className,
  ...props
}: ScoreRingProps) {
  const config = sizeConfig[size];
  const radius = (config.diameter - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = Math.min((score / max) * 100, 100);
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const scoreColor = color || getScoreColor(score);

  return (
    <div
      className={twMerge(clsx('relative inline-flex items-center justify-center', className))}
      style={{ width: config.diameter, height: config.diameter }}
      {...props}
    >
      <svg
        className="transform -rotate-90"
        width={config.diameter}
        height={config.diameter}
      >
        {/* Background ring */}
        <circle
          cx={config.diameter / 2}
          cy={config.diameter / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={config.strokeWidth}
          className="text-neutral-200"
        />
        {/* Progress ring */}
        <circle
          cx={config.diameter / 2}
          cy={config.diameter / 2}
          r={radius}
          fill="none"
          stroke={scoreColor}
          strokeWidth={config.strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-500 ease-out"
        />
      </svg>
      {showLabel && (
        <div className={clsx('absolute inset-0 flex items-center justify-center', config.fontSize)}>
          <span className="font-semibold text-neutral-900">
            {label ?? score}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Horizontal score bar (alternative to ring)
 */
export interface ScoreBarProps {
  score: number;
  max?: number;
  label?: string;
  showValue?: boolean;
}

export function ScoreBar({ score, max = 10, label, showValue = true }: ScoreBarProps) {
  const percentage = Math.min((score / max) * 100, 100);
  const color = getScoreColor(score);

  return (
    <div className="space-y-1">
      {(label || showValue) && (
        <div className="flex items-center justify-between text-sm">
          {label && <span className="text-neutral-600">{label}</span>}
          {showValue && <span className="font-medium text-neutral-900">{score}/{max}</span>}
        </div>
      )}
      <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${percentage}%`,
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
}

/**
 * Multi-score display for review scores
 */
export interface ScoreGridProps {
  scores: {
    label: string;
    value: number;
    max?: number;
  }[];
}

export function ScoreGrid({ scores }: ScoreGridProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {scores.map((score) => (
        <div key={score.label} className="text-center">
          <ScoreRing score={score.value} max={score.max} size="md" />
          <p className="mt-1 text-xs text-neutral-500">{score.label}</p>
        </div>
      ))}
    </div>
  );
}
